// Action-request execution (T1.3 / REQ-62 / D-5 REVERSED)
//
// The agent NEVER writes — it proposes a typed action_request; the UI renders
// confirm/cancel and, on confirm, performs the write here via the trips REST
// API with the user's own JWT. Shared by every chat surface so proposals
// behave identically everywhere.

import {
  createActivity,
  createTrip,
  deleteActivity,
  updateActivity,
  updateTrip,
} from './api/tripsApi';
import { extractTripUuid } from './utils/idUtils';

export type ActionRequestAction =
  | 'CREATE_TRIP'
  | 'UPDATE_TRIP'
  | 'ADD_ACTIVITIES'
  | 'UPDATE_ACTIVITY'
  | 'DELETE_ACTIVITY';

export interface ActionRequest {
  action: ActionRequestAction;
  trip_id?: string;
  payload: Record<string, any>;
}

export type ActionResolution = 'confirmed' | 'dismissed' | 'failed';

export interface ActionResult {
  note: string;
  tripId?: string;
}

// Chat-surface context threaded into the write. `conversationId` links the
// created trip back to the chat that planned it (shown on the trips page and
// re-opened when the trip's chat is resumed).
export interface ActionContext {
  conversationId?: string;
  // Safety net: when the agent's CREATE_TRIP payload carries no `activities`
  // but it recommended specific places in the SAME message (the chips shown
  // under the card), save those as (untimed) stops so the trip isn't created
  // with only district names. Redundant once the agent embeds activities in the
  // payload, but harmless — payload activities always take precedence.
  fallbackActivities?: Array<Record<string, any>>;
}

// Map an agent activity proposal (calendar-ready shape: {name, description,
// date, start_time, end_time, place_id, location_text}) → the trips API's
// CreateActivity DTO. Shared by CREATE_TRIP and ADD_ACTIVITIES.
const toActivityCreate = (activity: any) => ({
  title: activity?.name || activity?.title || 'Activity',
  location: activity?.location_text || activity?.location || activity?.name || activity?.title || '',
  dateTime: actionDateToIso(activity?.date, activity?.start_time),
});

export const ACTION_LABELS: Record<ActionRequestAction, string> = {
  CREATE_TRIP: 'Create this trip',
  UPDATE_TRIP: 'Update this trip',
  ADD_ACTIVITIES: 'Add these activities',
  UPDATE_ACTIVITY: 'Update this activity',
  DELETE_ACTIVITY: 'Remove this activity',
};

// MM-DD-YYYY (+ optional HH:MM) → ISO 8601 for the trips API
export const actionDateToIso = (date?: string, time?: string): string | undefined => {
  if (!date) return undefined;
  const parts = date.split('-');
  if (parts.length !== 3) return undefined;
  const [mm, dd, yyyy] = parts;
  return `${yyyy}-${mm}-${dd}T${time && /^\d{2}:\d{2}$/.test(time) ? time : '09:00'}:00`;
};

// The agent may return destinations as an array, an array of {name}, or a
// comma-separated string. The trips API strictly requires a string[]; coerce.
export const toDestinationArray = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === 'string' ? v : v?.name || v?.title || '')).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

// Build the trips API's typed dates object, or undefined when no dates given.
const toTripDates = (dates: any) => {
  const startDate = actionDateToIso(dates?.startDate)?.slice(0, 10);
  const endDate = actionDateToIso(dates?.endDate)?.slice(0, 10);
  return startDate && endDate ? { type: 'concrete', startDate, endDate } : undefined;
};

// The server requires travelers (when present) to be { adults >= 1, children >= 0 }
// as integers. The agent may omit children or send strings — coerce, or omit
// entirely when no travelers were given.
const toTravelers = (value: any): { adults: number; children: number } | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const adults = Number.parseInt(value.adults, 10);
  const children = Number.parseInt(value.children, 10);
  return {
    adults: Number.isInteger(adults) && adults >= 1 ? adults : 1,
    children: Number.isInteger(children) && children >= 0 ? children : 0,
  };
};

// Executes a confirmed proposal. Returns a user-facing note and optional trip id; throws on failure.
export const executeActionRequest = async (
  request: ActionRequest,
  context: ActionContext = {},
): Promise<ActionResult> => {
  const payload = request.payload || {};
  switch (request.action) {
    case 'CREATE_TRIP': {
      const destinations = toDestinationArray(payload.destinations);
      const budget =
        payload.budget && typeof payload.budget === 'object' && payload.budget.amount
          ? { amount: Number(payload.budget.amount), currency: payload.budget.currency || 'USD' }
          : undefined;
      const created = await createTrip({
        title: payload.title || destinations.join(', ') || 'New trip',
        destinations,
        dates: toTripDates(payload.dates),
        status: 'planning',
        budget,
        travelers: toTravelers(payload.travelers),
        description: payload.description || undefined,
        // Link the trip back to the chat that planned it.
        conversation_id: context.conversationId || undefined,
      } as any);
      const tripId = extractTripUuid(created.id);

      // The itinerary planned in chat rides along on the CREATE_TRIP payload as
      // `activities`, so the trip is created WITH its stops in one confirm. The
      // trip already exists at this point — a failed activity must not discard
      // it, so we add them best-effort and report how many landed. When the
      // payload has no activities, fall back to the message's recommended places.
      const payloadActivities = Array.isArray(payload.activities) ? payload.activities : [];
      const proposedActivities = payloadActivities.length
        ? payloadActivities
        : (context.fallbackActivities || []);
      let added = 0;
      for (const activity of proposedActivities) {
        const dto = toActivityCreate(activity);
        if (!dto.title) continue;
        try {
          await createActivity(tripId, dto);
          added += 1;
        } catch (error) {
          console.error('Failed to add activity during trip creation:', error);
        }
      }

      const title = (created as any)?.title || payload.title || 'trip';
      const note = added
        ? `Trip "${title}" created with ${added} ${added === 1 ? 'stop' : 'stops'}`
        : `Trip "${title}" created`;
      return { note, tripId };
    }
    case 'UPDATE_TRIP': {
      if (!request.trip_id) throw new Error('missing trip_id');
      const { dates, destinations, travelers, ...rest } = payload;
      await updateTrip({
        id: request.trip_id,
        ...rest,
        ...(destinations !== undefined ? { destinations: toDestinationArray(destinations) } : {}),
        ...(dates !== undefined ? { dates: toTripDates(dates) } : {}),
        ...(travelers !== undefined ? { travelers: toTravelers(travelers) } : {}),
      } as any);
      return { note: 'Trip updated' };
    }
    case 'ADD_ACTIVITIES': {
      if (!request.trip_id) throw new Error('missing trip_id');
      const activities = payload.activities || [];
      if (!activities.length) throw new Error('no activities in proposal');
      for (const activity of activities) {
        await createActivity(request.trip_id, toActivityCreate(activity));
      }
      return { note: `Added ${activities.length} ${activities.length === 1 ? 'activity' : 'activities'}` };
    }
    case 'UPDATE_ACTIVITY': {
      if (!request.trip_id || !payload.activity_id) throw new Error('missing trip_id/activity_id');
      const { activity_id, ...changes } = payload;
      await updateActivity(request.trip_id, activity_id, {
        ...(changes.name ? { title: changes.name } : {}),
        ...(changes.location_text ? { location: changes.location_text } : {}),
        ...(changes.date || changes.start_time
          ? { dateTime: actionDateToIso(changes.date, changes.start_time) }
          : {}),
      });
      return { note: 'Activity updated' };
    }
    case 'DELETE_ACTIVITY': {
      if (!request.trip_id || !payload.activity_id) throw new Error('missing trip_id/activity_id');
      await deleteActivity(request.trip_id, payload.activity_id);
      return { note: 'Activity removed' };
    }
    default:
      throw new Error(`Unknown action ${(request as any).action}`);
  }
};
